/**
 * Who is in this tenant, and what they may do.
 *
 * Scoped to memberships, never to `users` — a platform-wide list would show one
 * customer's screen the names of another's staff.
 *
 * ── What this screen cannot do, on purpose ───────────────────────────────
 *
 * It does not set passwords. Resetting somebody else's credential is a
 * different act with different consequences and it already has its own route;
 * putting it behind the screen people use for routine role changes is how it
 * gets done casually.
 *
 * ── Two edits are refused by the server, and shown as refused here ───────
 *
 * An admin cannot demote or disable themselves, and the last enabled
 * owner/admin cannot be demoted or disabled by anyone. Neither is recoverable
 * from inside the product — the tenant simply has nobody left who can undo it.
 * The controls are disabled with the reason attached rather than hidden,
 * because a missing button teaches nothing.
 *
 * The server enforces both regardless of what this screen does; this only saves
 * the round trip and explains why.
 *
 * ── Shape ────────────────────────────────────────────────────────────────
 *
 * A read-only list; a row opens a drawer with the role, the enable/disable
 * action and the way into the portal-grants drawer. The role used to change
 * the moment a `<select>` in the table changed, which is the one kind of
 * control this screen should not have — a misclick on the last admin's row
 * was a round trip to a refusal rather than a thing that never left the page.
 */

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
  type ColumnDef,
} from './components/index.ts';
import { TenantInvites } from './Invites.tsx';
import { UserSitesDrawer } from './UserSites.tsx';

const ROLES = ['owner', 'admin', 'operator', 'viewer', 'client'] as const;
const ADMINISTRATIVE: readonly string[] = ['owner', 'admin'];

interface UserRow {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  disabled: boolean;
  hasMfa: boolean;
  joinedAt: string;
  isSelf: boolean;
  /** May open remote sessions to this organisation's hosts (069). */
  remoteAccess: boolean;
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

export function UsersView(): React.JSX.Element {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<UserRow | null>(null);
  /** The client whose portal sites are being edited, if any. */
  const [editingSites, setEditingSites] = useState<UserRow | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers((await api.get<{ users: UserRow[] }>('/api/users')).users);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<UserRow, unknown>[]>(
    () => [
      {
        id: 'person',
        header: 'Person',
        accessorFn: (u) => u.displayName ?? u.email,
        cell: ({ row }) => (
          <div className="stack-tight">
            <span>
              {row.original.displayName ?? row.original.email}
              {row.original.isSelf && <span className="muted"> — you</span>}
            </span>
            <span className="muted small">{row.original.email}</span>
          </div>
        ),
      },
      { id: 'role', header: 'Role', accessorKey: 'role', size: 100 },
      {
        id: 'mfa',
        header: 'Second factor',
        accessorFn: (u) => (u.hasMfa ? 1 : 0),
        cell: ({ row }) =>
          row.original.hasMfa ? (
            'enrolled'
          ) : (
            // Worth showing rather than leaving blank: an admin without one is
            // a different risk from an operator without one.
            <span className="muted">none</span>
          ),
        size: 120,
      },
      {
        id: 'remote',
        header: 'Remote access',
        accessorFn: (u) => (u.remoteAccess ? 1 : 0),
        cell: ({ row }) => (row.original.remoteAccess ? 'granted' : <span className="muted">—</span>),
        size: 120,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (u) => (u.disabled ? 'disabled' : 'active'),
        cell: ({ row }) =>
          row.original.disabled ? (
            <Pill state="stale" reason="Signed out, and cannot sign in until enabled again.">
              disabled
            </Pill>
          ) : (
            <Pill state="ok">active</Pill>
          ),
        size: 96,
      },
      {
        id: 'joined',
        header: 'Joined',
        accessorKey: 'joinedAt',
        cell: ({ getValue }) => (
          <span className="muted">{new Date(getValue() as string).toLocaleDateString()}</span>
        ),
        size: 110,
      },
    ],
    [],
  );

  if (error !== null && users === null) return <Banner tone="fault">{error}</Banner>;
  if (users === null) return <Loading rows={5} cols={5} label="Loading users" />;

  /** Enabled administrators, which is what the last-admin rule counts. */
  const admins = users.filter((u) => ADMINISTRATIVE.includes(u.role) && !u.disabled);

  return (
    <>
      {error !== null && <Banner tone="fault">{error}</Banner>}

      <TenantInvites callerRole={users.find((u) => u.isSelf)?.role ?? null} />

      <Section
        flush
        title="People"
        summary={`${users.length} member${users.length === 1 ? '' : 's'} of this tenant · ${admins.length} can administer it`}
      >
        <DataTable
          data={users}
          columns={columns}
          getRowId={(u) => u.id}
          initialSort={[{ id: 'person', desc: false }]}
          onRowClick={setSelected}
          rowClass={(u) => (u.disabled ? 'row-muted' : undefined)}
          caption="Members of this tenant"
          empty={<Empty title="Nobody is in this tenant" />}
        />
      </Section>

      {selected !== null && (
        <UserDrawer
          user={selected}
          lastAdmin={ADMINISTRATIVE.includes(selected.role) && !selected.disabled && admins.length === 1}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            void load();
          }}
          onEditSites={() => {
            // One drawer at a time: two panels from the right of the same
            // width would sit exactly on top of each other.
            setSelected(null);
            setEditingSites(selected);
          }}
        />
      )}

      {editingSites !== null && (
        <UserSitesDrawer
          userId={editingSites.id}
          email={editingSites.email}
          role={editingSites.role}
          onClose={() => setEditingSites(null)}
        />
      )}
    </>
  );
}

function UserDrawer({
  user,
  lastAdmin,
  onClose,
  onChanged,
  onEditSites,
}: {
  user: UserRow;
  /** The only enabled owner/admin left — the person the last-admin rule protects. */
  lastAdmin: boolean;
  onClose: () => void;
  onChanged: () => void;
  onEditSites: () => void;
}): React.JSX.Element {
  const [role, setRole] = useState(user.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Removal is two presses: the first arms it, the second does it. */
  const [confirmRemove, setConfirmRemove] = useState(false);

  const roleLocked = user.isSelf || lastAdmin;
  const roleReason = user.isSelf
    ? 'You cannot change your own role — ask another admin.'
    : lastAdmin
      ? 'The only person who can administer this tenant. Promote somebody else first.'
      : 'Takes effect on their next request.';

  const disableLocked = !user.disabled && roleLocked;
  const disableReason = user.disabled
    ? 'Lets this person sign in again.'
    : user.isSelf
      ? 'You cannot disable your own account.'
      : lastAdmin
        ? 'The only person who can administer this tenant.'
        : 'Moves them out of this tenant immediately. Any other tenant they work for is unaffected.';

  const change = async (patch: { role?: string; disabled?: boolean }): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/users/${user.id}`, patch);
      onChanged();
    } catch (err) {
      // The server's refusals carry the reasoning. Showing it beats a
      // generic failure for a rule the person has just run into.
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const id = `user-${user.id}`;

  return (
    <Drawer
      open
      onClose={onClose}
      title={user.displayName ?? user.email}
      subtitle={`${user.email} · ${user.role}${user.isSelf ? ' · you' : ''}`}
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        <Field id={`${id}-role`} label="Role" hint={roleReason}>
          <select
            id={`${id}-role`}
            value={role}
            disabled={busy || roleLocked}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>

        <div className="row">
          <Button
            variant="primary"
            busy={busy}
            disabled={roleLocked || role === user.role}
            onClick={() => void change({ role })}
          >
            Save
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>

        {/*
          Remote access (069): whether this person may open remote sessions to
          this organisation's hosts. Staff roles only; withdrawing it ends any
          session they have open. The switches on the Remote access screen say
          which hosts accept sessions at all.
        */}
        <Field
          id={`${id}-remote`}
          label="Remote access"
          hint={
            ['owner', 'admin', 'operator'].includes(user.role)
              ? 'May open remote sessions (shell, desktop) to this organisation’s hosts, with a reason and an authenticator code.'
              : `A ${user.role} cannot hold remote access.`
          }
        >
          <label className="row-tight">
            <input
              id={`${id}-remote`}
              type="checkbox"
              checked={user.remoteAccess}
              disabled={busy || !['owner', 'admin', 'operator'].includes(user.role)}
              onChange={async (e) => {
                const enabled = e.target.checked;
                setBusy(true);
                setError(null);
                try {
                  await api.put(`/api/users/${user.id}/remote-access`, { enabled });
                  onChanged();
                } catch (err) {
                  setError(failure(err));
                } finally {
                  setBusy(false);
                }
              }}
            />
            {user.remoteAccess ? 'Granted' : 'Not granted'}
          </label>
        </Field>

        {/*
          Disabled with the reason attached rather than hidden — the same rule
          this screen already applies to the last-admin controls, and for the
          same reason: a missing button teaches nothing. The server refuses
          grants on a non-client too, and says why; this saves the round trip
          and explains it in place.

          Grants on a non-client are INERT rather than harmless. They would sit
          in the table doing nothing, look configured, and take effect silently
          if the role ever changed to client.
        */}
        <Field
          id={`${id}-sites`}
          label="Portal sites"
          hint={
            user.role === 'client'
              ? 'Which sites this customer sees in the portal.'
              : `${user.role} accounts already see every site in this tenant. Site grants apply to client members only.`
          }
        >
          <div>
            <Button id={`${id}-sites`} disabled={busy || user.role !== 'client'} onClick={onEditSites}>
              Choose sites
            </Button>
          </div>
        </Field>

        <Field id={`${id}-access`} label="Access" hint={`${disableReason} Passwords are not set here.`}>
          <div>
            {user.disabled ? (
              <Button id={`${id}-access`} busy={busy} onClick={() => void change({ disabled: false })}>
                Enable
              </Button>
            ) : (
              <Button
                id={`${id}-access`}
                variant="danger"
                busy={busy}
                disabled={disableLocked}
                onClick={() => void change({ disabled: true })}
              >
                Disable
              </Button>
            )}
          </div>
        </Field>

        <Field
          id={`${id}-remove`}
          label="Remove from this tenant"
          hint={
            user.isSelf
              ? 'You cannot remove yourself — ask another admin.'
              : lastAdmin
                ? 'The only person who can administer this tenant.'
                : 'Ends their access here and their portal site grants. Their account, and any other tenant they work for, are untouched.'
          }
        >
          <div className="row">
            <Button
              id={`${id}-remove`}
              variant="danger"
              busy={busy}
              disabled={user.isSelf || lastAdmin}
              onClick={async () => {
                if (!confirmRemove) {
                  setConfirmRemove(true);
                  return;
                }
                setBusy(true);
                setError(null);
                try {
                  await api.del(`/api/users/${user.id}`);
                  onChanged();
                } catch (err) {
                  setError(failure(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmRemove ? `Remove ${user.displayName ?? user.email}` : 'Remove'}
            </Button>
            {confirmRemove && (
              <Button variant="ghost" onClick={() => setConfirmRemove(false)}>
                Keep them
              </Button>
            )}
          </div>
        </Field>
      </div>
    </Drawer>
  );
}
